// client/src/coms/components/SoundboardPanel.js
// The soundboard popover opened from the voice bar (bottom-left controls). One click on a sound plays it
// for everyone in the current voice channel; the small ▶ plays it only for you. Tabs: This Server, My
// Sounds (personal packs, installed packs and clips on this device), Other Servers, Favourites, Recent.
// What may be played is decided by the server (GET /sounds/library says why something is unavailable);
// this panel only mirrors it. Managing sounds happens in Server Settings → Soundboard and Settings →
// Soundboard, both reachable from here.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { comsApiDelete, comsApiGet, comsApiPut } from '../comsApi';
import { listLocalSounds, updateLocalSound } from '../soundboard/soundStore';
import {
  getAudioState,
  noteServerTime,
  prefetchSounds,
  previewSound,
  readPrefs,
  stopChannelSounds,
  stopPreview,
  subscribeSoundboardActivity,
  triggerSound,
  unlockAudio,
  writePrefs,
} from '../soundboard/soundboardEngine';
import { getVoiceMasterVolume, setVoiceMasterVolume } from '../voip/voiceMasterVolume';
import '../soundboard/soundboard.css';

const TAB_KEY = 'vox:soundboardTab';
const TABS = [
  { key: 'server', label: 'This Server' },
  { key: 'mine', label: 'My Sounds' },
  { key: 'other', label: 'Other Servers' },
  { key: 'fav', label: 'Favourites' },
  { key: 'recent', label: 'Recent' },
];
const COOLDOWN_CODES = new Set(['cooldown', 'channel_cooldown', 'channel_busy', 'rate_limited']);
const REFRESH_CODES = new Set(['soundboard_disabled', 'channel_paused', 'source_disabled', 'approved_only', 'missing_permission', 'source_local_only', 'source_members_only', 'source_restricted', 'sound_not_found']);

function errText(e, fallback) {
  return String((e && e.data && e.data.error) || (e && e.message) || fallback);
}

export function shortcutFromEvent(e) {
  const k = String(e.key || '');
  if (!k || ['Control', 'Shift', 'Alt', 'Meta'].includes(k)) return '';
  const parts = [];
  if (e.ctrlKey) parts.push('Ctrl');
  if (e.altKey) parts.push('Alt');
  if (e.shiftKey) parts.push('Shift');
  if (e.metaKey) parts.push('Meta');
  if (!parts.length) return ''; // require a modifier so typing never triggers sounds
  parts.push(k.length === 1 ? k.toUpperCase() : k);
  return parts.join('+');
}

// Device clips are personal sounds: the same rules the server applies to them, from the library context.
export function localBlock(context) {
  if (!context || !context.in_voice) return ['not_in_voice', 'Join a voice channel to play sounds for everyone'];
  const p = context.policy || {};
  const can = context.can || {};
  if (!p.enabled) return ['soundboard_disabled', 'The soundboard is turned off on this server'];
  if (p.channel && p.channel.paused) return ['channel_paused', 'The soundboard is paused in this channel'];
  if (!can.use_soundboard) return ['missing_permission', 'You do not have permission to use the soundboard here'];
  if (p.approved_only) return ['approved_only', 'Only sounds approved for this server can be played here'];
  if (!can.use_external_sounds) return ['missing_permission', 'You do not have permission to use your own sounds here'];
  if (!p.allow_personal_sounds) return ['source_disabled', 'Personal sounds are turned off on this server'];
  return ['', ''];
}

// Search: name, keywords, description, pack, server and creator.
export function matchesQuery(item, q) {
  if (!q) return true;
  const hay = [item.name, item.description, item.collection_name, item.server_name, item.creator, ...(item.keywords || [])].join(' ').toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((w) => hay.includes(w));
}

function friendly(code, fallback) {
  if (code === 'cooldown') return fallback || 'Cooling down…';
  if (code === 'channel_busy') return 'Another sound is playing in this channel.';
  if (code === 'not_in_voice') return 'Join the voice channel to play sounds.';
  return fallback;
}

export default function SoundboardPanel({ open, onClose, roomName, serverKey, participants = [], onManageServer, onOpenSettings }) {
  const [lib, setLib] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [localSounds, setLocalSounds] = useState([]);
  const [tab, setTab] = useState(() => {
    try {
      return localStorage.getItem(TAB_KEY) || 'server';
    } catch {
      return 'server';
    }
  });
  const [query, setQuery] = useState('');
  const [notice, setNotice] = useState(null); // { text, tone }
  const [cool, setCool] = useState({ until: 0, total: 0 });
  const [playingKeys, setPlayingKeys] = useState({}); // key -> { until, eventId, mine }
  const [audioState, setAudioState] = useState(getAudioState);
  const [mixOpen, setMixOpen] = useState(false);
  const [prefs, setPrefs] = useState(readPrefs);
  const [voiceVol, setVoiceVol] = useState(getVoiceMasterVolume);
  const [menu, setMenu] = useState(null); // { x, y, item }
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [previewing, setPreviewing] = useState('');
  const [lastPlayed, setLastPlayed] = useState(null);
  const [tick, setTick] = useState(0);
  const searchRef = useRef(null);
  const bodyRef = useRef(null);
  const popRef = useRef(null);

  const load = useCallback(async () => {
    if (!open) return;
    const params = new URLSearchParams();
    if (roomName) params.set('room', roomName);
    if (serverKey) params.set('server', serverKey);
    try {
      const sentAt = Date.now();
      const r = await comsApiGet(`/sounds/library?${params.toString()}`);
      noteServerTime(r && r.server_ts, sentAt);
      setLib(r);
      setLoadErr('');
      const c = r && r.context;
      if (c && c.cooldown_remaining_ms > 0) setCool({ until: Date.now() + c.cooldown_remaining_ms, total: (c.policy && c.policy.user_cooldown_ms) || c.cooldown_remaining_ms });
      const sounds = Array.isArray(r && r.sounds) ? r.sounds : [];
      prefetchSounds([...sounds.filter((s) => s.favorite), ...sounds.filter((s) => s.tab === 'server' && s.playable)], 16);
    } catch (e) {
      setLoadErr(errText(e, 'Could not load sounds.'));
    }
    try {
      setLocalSounds(await listLocalSounds());
    } catch {}
  }, [open, roomName, serverKey]);

  useEffect(() => {
    if (!open) return undefined;
    load();
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, [open, load]);

  useEffect(() => {
    try {
      localStorage.setItem(TAB_KEY, tab);
    } catch {}
  }, [tab]);

  // Policy changes, sounds playing (anyone's), stops, and the browser's audio state.
  useEffect(
    () =>
      subscribeSoundboardActivity((a) => {
        if (a.type === 'prefs') setPrefs(readPrefs());
        if (a.type === 'policy') load();
        if (a.type === 'audio_state') setAudioState(a.state);
        if (a.type === 'blocked') {
          setAudioState('suspended');
        }
        if (a.type === 'play') {
          const key = a.soundId ? `s:${a.soundId}` : `h:${a.hash}`;
          setPlayingKeys((m) => ({ ...m, [key]: { until: Date.now() + Math.max(600, a.durationMs || 1500), eventId: a.eventId, userId: a.userId } }));
          setLastPlayed({ ...a, at: Date.now() });
        }
        if (a.type === 'stopped') {
          setPlayingKeys((m) => (a.all ? {} : Object.fromEntries(Object.entries(m).filter(([, v]) => !(a.eventIds || []).includes(v.eventId)))));
        }
        if (a.type === 'ended') setPlayingKeys((m) => Object.fromEntries(Object.entries(m).filter(([, v]) => v.eventId !== a.eventId)));
      }),
    [load]
  );

  // Animation clock while something is cooling down or playing.
  const busy = cool.until > Date.now() || Object.keys(playingKeys).length > 0;
  useEffect(() => {
    if (!open || !busy) return undefined;
    const t = setInterval(() => {
      setTick((n) => n + 1);
      const now = Date.now();
      setPlayingKeys((m) => {
        const next = Object.fromEntries(Object.entries(m).filter(([, v]) => v.until > now));
        return Object.keys(next).length === Object.keys(m).length ? m : next;
      });
    }, 100);
    return () => clearInterval(t);
  }, [open, busy]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(null), notice.tone === 'err' ? 6000 : 3500);
    return () => clearTimeout(t);
  }, [notice]);

  useEffect(() => {
    if (!open) {
      setMenu(null);
      setMixOpen(false);
      stopPreview();
      setPreviewing('');
    }
  }, [open]);

  // Clicking anywhere else closes the popover, and that click still reaches what was clicked (mute,
  // disconnect, a channel...). The soundboard button toggles it itself.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (popRef.current && popRef.current.contains(t)) return;
      if (t.closest && (t.closest('[data-testid="sidebar-soundboard"]') || t.closest('.vox-sb-menu'))) return;
      onClose();
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [open, onClose]);

  // Escape closes (menu first); "/" jumps to search.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (menu) setMenu(null);
        else if (mixOpen) setMixOpen(false);
        else onClose();
      } else if (e.key === '/' && document.activeElement !== searchRef.current && !/input|textarea/i.test(String((e.target && e.target.tagName) || ''))) {
        e.preventDefault();
        if (searchRef.current) searchRef.current.focus();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, menu, mixOpen, onClose]);

  const context = lib && lib.context;
  const inVoice = !!(context && context.in_voice && roomName);

  const items = useMemo(() => {
    const out = (Array.isArray(lib && lib.sounds) ? lib.sounds : []).map((s) => ({ ...s, key: `s:${s.id}`, kind: 'col' }));
    const [code, reason] = localBlock(context);
    for (const s of localSounds) {
      out.push({
        key: `l:${s.id}`,
        kind: 'local',
        id: s.id,
        name: s.name,
        emoji: s.emoji,
        keywords: [],
        description: '',
        collection_name: 'This device',
        hash: s.serverHash || '',
        playKey: s.serverHash ? `h:${s.serverHash}` : '',
        volume: s.volume,
        favorite: !!s.favorite,
        tab: 'mine',
        source: 'personal',
        local: s,
        can_edit: true,
        playable: !code,
        blocked_code: code,
        blocked_reason: reason,
      });
    }
    return out;
  }, [lib, localSounds, context]);

  const q = query.trim();
  const byTab = useMemo(() => {
    const t = { server: [], mine: [], other: [], fav: [], recent: [] };
    for (const it of items) {
      if (t[it.tab]) t[it.tab].push(it);
      if (it.favorite) t.fav.push(it);
    }
    const find = (ref) => items.find((it) => it.key === ref || (ref.startsWith('h:') && it.playKey === ref));
    const recent = (lib && lib.recent) || [];
    t.recent = recent.map(find).filter(Boolean);
    t.frequent = ((lib && lib.frequent) || []).map(find).filter(Boolean);
    return t;
  }, [items, lib]);

  const visibleTabs = TABS.filter((t) => t.key !== 'server' || (context && context.server));
  const activeTab = visibleTabs.some((t) => t.key === tab) ? tab : visibleTabs[0].key;

  const coolLeft = Math.max(0, cool.until - Date.now());

  function flash(text, tone = '') {
    setNotice({ text, tone });
  }

  async function play(item) {
    setMenu(null);
    if (!item.playable) {
      flash(item.blocked_reason || 'This sound cannot be played here.', 'warn');
      return;
    }
    if (coolLeft > 0) {
      flash(`Cooling down: ${Math.ceil(coolLeft / 1000)} s`, 'warn');
      return;
    }
    try {
      const r = await triggerSound(roomName, item.kind === 'local' ? item.local : { id: item.id, hash: item.hash }, {
        onServerHash: (h) => {
          if (item.kind === 'local') updateLocalSound(item.id, { serverHash: h }).then(() => listLocalSounds().then(setLocalSounds)).catch(() => {});
        },
      });
      if (r && r.cooldown_ms) setCool({ until: Date.now() + r.cooldown_ms, total: r.cooldown_ms });
    } catch (e) {
      const code = (e && e.code) || (e && e.data && e.data.code) || '';
      const retry = Number(e && e.data && e.data.retry_after_ms) || 0;
      if (COOLDOWN_CODES.has(code) && retry > 0) setCool({ until: Date.now() + retry, total: Math.max(retry, cool.total || 0) });
      flash(friendly(code, errText(e, 'Could not play the sound.')), COOLDOWN_CODES.has(code) ? 'warn' : 'err');
      if (REFRESH_CODES.has(code)) load();
    }
  }

  async function preview(item) {
    if (previewing === item.key) {
      stopPreview();
      setPreviewing('');
      return;
    }
    setPreviewing(item.key);
    try {
      if (item.kind === 'local') await previewSound({ blob: item.local.wav, volume: item.volume });
      else await previewSound({ hash: item.hash, url: item.url, volume: item.volume });
    } catch (e) {
      flash(errText(e, 'Could not preview that sound.'), 'err');
    } finally {
      setPreviewing((k) => (k === item.key ? '' : k));
    }
  }

  async function toggleFav(item) {
    const next = !item.favorite;
    try {
      if (item.kind === 'local') {
        await updateLocalSound(item.id, { favorite: next });
        setLocalSounds(await listLocalSounds());
      } else {
        setLib((l) => (l ? { ...l, sounds: l.sounds.map((s) => (s.id === item.id ? { ...s, favorite: next } : s)) } : l));
        await (next ? comsApiPut(`/sounds/sounds/${item.id}/favorite`, {}) : comsApiDelete(`/sounds/sounds/${item.id}/favorite`));
      }
    } catch (e) {
      flash(errText(e, 'Could not update favourites.'), 'err');
      load();
    }
  }

  async function removeSound(item) {
    setConfirmDelete(null);
    try {
      await comsApiDelete(`/sounds/sounds/${item.id}`);
      flash(`Removed “${item.name}”.`);
      load();
    } catch (e) {
      flash(errText(e, 'Could not remove the sound.'), 'err');
    }
  }

  function openMenu(e, item) {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX || rect.left + rect.width / 2;
    const y = e.clientY || rect.bottom;
    setMenu({ x: Math.min(x, window.innerWidth - 190), y: Math.min(y, window.innerHeight - 180), item });
  }

  function manage(item) {
    setMenu(null);
    if (item && item.tab === 'server' && typeof onManageServer === 'function') onManageServer();
    else if (typeof onOpenSettings === 'function') onOpenSettings();
  }

  // Arrow keys move between tiles; Enter plays; P previews; F favourites; the context-menu key opens the menu.
  function onGridKey(e) {
    const tiles = Array.from((bodyRef.current && bodyRef.current.querySelectorAll('.vox-sb-tile')) || []);
    const i = tiles.indexOf(document.activeElement);
    if (i < 0) return;
    const item = items.find((it) => it.key === tiles[i].dataset.soundKey);
    const cols = Math.max(1, tiles.filter((t) => t.offsetTop === tiles[0].offsetTop).length);
    let j = -1;
    if (e.key === 'ArrowRight') j = i + 1;
    else if (e.key === 'ArrowLeft') j = i - 1;
    else if (e.key === 'ArrowDown') j = i + cols;
    else if (e.key === 'ArrowUp') j = i - cols;
    else if ((e.key === 'p' || e.key === 'P') && item) {
      e.preventDefault();
      preview(item);
      return;
    } else if ((e.key === 'f' || e.key === 'F') && item) {
      e.preventDefault();
      toggleFav(item);
      return;
    } else if ((e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) && item) {
      openMenu(e, item);
      return;
    }
    if (j >= 0 && j < tiles.length) {
      e.preventDefault();
      tiles[j].focus();
    }
  }

  const me = lib && lib.me != null ? String(lib.me) : '';
  const people = useMemo(() => (participants || []).filter((p) => p && p.userId && String(p.userId) !== me), [participants, me]);
  const nameOf = (uid) => {
    if (me && String(uid) === me) return 'you';
    const p = (participants || []).find((x) => String(x.userId) === String(uid));
    return p ? p.name || `User ${uid}` : 'someone';
  };
  const anyPlaying = Object.keys(playingKeys).length > 0;
  const canStopAll = !!(context && context.can && context.can.stop);
  const canManageServer = !!(context && context.can && (context.can.upload || context.can.manage_sounds || context.can.manage_settings));

  if (!open) return null;

  function tile(item) {
    const pk = item.kind === 'local' ? item.playKey : item.key;
    const isPlaying = !!(pk && playingKeys[pk]);
    const sub =
      item.kind === 'local'
        ? 'This device'
        : item.tab === 'other'
          ? item.server_name
          : item.source === 'marketplace' || (item.tab === 'mine' && item.creator && !item.can_edit)
            ? `by ${item.creator || 'someone'}`
            : activeTab === 'fav' || activeTab === 'recent' || q
              ? item.server_name || item.collection_name
              : '';
    const title = `${item.name}${item.description ? ` — ${item.description}` : ''}${item.playable ? '' : `\n${item.blocked_reason}`}`;
    const coolPct = coolLeft > 0 && cool.total ? coolLeft / cool.total : 0;
    return (
      <div key={item.key} className="vox-sb-cell">
        <button
          type="button"
          className={`vox-sb-tile${item.playable ? '' : ' is-disabled'}${isPlaying ? ' is-playing' : ''}`}
          data-sound-key={item.key}
          data-testid="sb-tile"
          aria-disabled={!item.playable}
          aria-label={`Play ${item.name} for everyone${item.playable ? '' : ` (unavailable: ${item.blocked_reason})`}`}
          title={title}
          onClick={() => play(item)}
          onContextMenu={(e) => openMenu(e, item)}
        >
          <span className="vox-sb-emoji" aria-hidden="true">
            {item.emoji || '🔊'}
          </span>
          <span className="vox-sb-tile-text">
            <span className="vox-sb-name">{item.name}</span>
            {isPlaying ? (
              <span className="vox-sb-eq" aria-label="Playing">
                <span />
                <span />
                <span />
              </span>
            ) : sub ? (
              <span className="vox-sb-src">{sub}</span>
            ) : null}
          </span>
          {coolPct > 0 && item.playable ? <span className="vox-sb-cool" style={{ width: '100%', transform: `scaleX(${coolPct})` }} /> : null}
        </button>
        <button type="button" className={`vox-sb-mini fav${item.favorite ? ' on' : ''}`} aria-label={item.favorite ? `Remove ${item.name} from favourites` : `Add ${item.name} to favourites`} title={item.favorite ? 'Favourite' : 'Add to favourites'} onClick={() => toggleFav(item)}>
          ★
        </button>
        <button type="button" className="vox-sb-mini preview" aria-label={previewing === item.key ? `Stop preview of ${item.name}` : `Preview ${item.name} (only you hear it)`} title="Preview (only you hear it)" onClick={() => preview(item)} data-testid="sb-preview">
          {previewing === item.key ? '■' : '▶'}
        </button>
      </div>
    );
  }

  const grid = (list) => <div className="vox-sb-grid">{list.map(tile)}</div>;

  function groups(list, by) {
    const map = new Map();
    for (const it of list) {
      const k = by(it) || '';
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(it);
    }
    if (map.size <= 1) return grid(list);
    return Array.from(map.entries()).map(([k, l]) => (
      <React.Fragment key={k}>
        <div className="vox-sb-group-title">{k}</div>
        {grid(l)}
      </React.Fragment>
    ));
  }

  function body() {
    if (!lib && !loadErr) return <div className="vox-sb-empty">Loading sounds…</div>;
    if (q) {
      const hits = items.filter((it) => matchesQuery(it, q));
      return hits.length ? groups(hits, (it) => TABS.find((t) => t.key === it.tab).label) : <div className="vox-sb-empty">No sounds match “{q}”.</div>;
    }
    const list = byTab[activeTab] || [];
    if (activeTab === 'server') {
      if (!list.length) {
        return (
          <div className="vox-sb-empty">
            This server has no sounds yet.
            {canManageServer ? (
              <div style={{ marginTop: 10 }}>
                <button type="button" className="coms-mini-btn primary" onClick={() => manage({ tab: 'server' })}>
                  Add server sounds
                </button>
              </div>
            ) : null}
          </div>
        );
      }
      return groups(list, (it) => it.collection_name);
    }
    if (activeTab === 'mine') {
      if (!list.length)
        return (
          <div className="vox-sb-empty">
            You have no sounds yet. Add some in Settings → Soundboard, or install packs from the Marketplace.
            <div style={{ marginTop: 10 }}>
              <button type="button" className="coms-mini-btn primary" onClick={() => manage(null)}>
                Add sounds
              </button>
            </div>
          </div>
        );
      return groups(list, (it) => it.collection_name);
    }
    if (activeTab === 'other') {
      if (!list.length) return <div className="vox-sb-empty">No sounds from your other servers. Servers can let their members use their sounds elsewhere.</div>;
      return groups(list, (it) => it.server_name);
    }
    if (activeTab === 'fav') return list.length ? grid(list) : <div className="vox-sb-empty">Star a sound (★) to keep it here.</div>;
    if (activeTab === 'recent') {
      if (!list.length) return <div className="vox-sb-empty">Sounds you play show up here.</div>;
      const freq = (byTab.frequent || []).filter((it) => !list.slice(0, 8).includes(it));
      return (
        <>
          {grid(list)}
          {freq.length ? (
            <>
              <div className="vox-sb-group-title">Most used</div>
              {grid(freq)}
            </>
          ) : null}
        </>
      );
    }
    return null;
  }

  const policy = context && context.policy;
  let banner = null;
  if (loadErr) banner = { tone: 'err', text: loadErr, action: ['Retry', load] };
  else if (!roomName) banner = { tone: '', text: 'Join a voice channel to play sounds for everyone. ▶ previews a sound just for you.' };
  else if (policy && !policy.enabled) banner = { tone: 'warn', text: 'The soundboard is turned off on this server.' };
  else if (policy && policy.channel && policy.channel.paused) banner = { tone: 'warn', text: `The soundboard is paused in this channel${policy.channel.until ? ` until ${new Date(policy.channel.until).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}.` };
  else if (context && context.can && !context.can.use_soundboard) banner = { tone: 'warn', text: 'You do not have permission to use the soundboard in this channel.' };
  else if (inVoice && audioState === 'suspended') banner = { tone: 'warn', text: 'Your browser paused sound playback, so you will not hear sounds.', action: ['Enable sound', () => unlockAudio().then((s) => setAudioState(s))] };
  else if (inVoice && prefs.muted) banner = { tone: '', text: 'Soundboards are muted for you. Others still hear your sounds.', action: ['Unmute', () => setPrefs(writePrefs({ muted: false }))] };

  const playingLine = lastPlayed && Date.now() - lastPlayed.at < 4000 ? `${lastPlayed.emoji || '🔊'} ${lastPlayed.name || 'A sound'} — ${nameOf(lastPlayed.userId)}${lastPlayed.audible ? '' : ' (muted)'}` : '';

  // Portalled to <body>: the sidebar can sit in a transformed mobile drawer, which would re-anchor
  // position: fixed and clip the popover.
  return createPortal(
    <>
      <div className="vox-sb-scrim" aria-hidden="true" />
      <div ref={popRef} className="vox-sb-pop" role="dialog" aria-modal="false" aria-label="Soundboard" data-testid="soundboard-panel" onMouseDown={() => menu && setMenu(null)}>
        <div className="vox-sb-head">
          <div className="vox-sb-title">
            Soundboard
            {context && context.server ? <small>{context.server.name}</small> : null}
          </div>
          {anyPlaying && inVoice && canStopAll ? (
            <button type="button" className="vox-sb-iconbtn danger" title="Stop all sounds in this channel" aria-label="Stop all sounds in this channel" onClick={() => stopChannelSounds(roomName).catch((e) => flash(errText(e, 'Could not stop the sounds.'), 'err'))}>
              ■
            </button>
          ) : null}
          <button type="button" className="vox-sb-iconbtn" aria-expanded={mixOpen} aria-label="Volume" title="Volume" onClick={() => setMixOpen((v) => !v)}>
            {prefs.muted ? '🔇' : '🔊'}
          </button>
          {canManageServer || onOpenSettings ? (
            <button type="button" className="vox-sb-iconbtn" aria-label="Manage sounds" title="Manage sounds" onClick={() => manage(canManageServer && activeTab === 'server' ? { tab: 'server' } : null)}>
              ⚙
            </button>
          ) : null}
          <button type="button" className="vox-sb-iconbtn" aria-label="Close soundboard" title="Close" onClick={onClose}>
            ✕
          </button>
        </div>

        {mixOpen ? (
          <div className="vox-sb-mix" data-testid="sb-mix">
            <span>Soundboard</span>
            <input type="range" min={0} max={1} step={0.05} value={prefs.volume} aria-label="Soundboard volume" onChange={(e) => setPrefs(writePrefs({ volume: Number(e.target.value) }))} />
            <span>{Math.round(prefs.volume * 100)}%</span>
            <span>Voices</span>
            <input type="range" min={0} max={1} step={0.05} value={voiceVol} aria-label="Voice volume" onChange={(e) => setVoiceVol(setVoiceMasterVolume(Number(e.target.value)))} />
            <span>{Math.round(voiceVol * 100)}%</span>
            <label style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={prefs.muted} onChange={(e) => setPrefs(writePrefs({ muted: e.target.checked }))} />
              Mute all soundboard sounds (only for me)
            </label>
            {people.length ? (
              <div className="people">
                {people.map((p) => {
                  const id = String(p.userId);
                  return (
                    <label key={id}>
                      <input type="checkbox" checked={!!prefs.mutedUsers[id]} onChange={(e) => setPrefs(writePrefs({ mutedUsers: { ...prefs.mutedUsers, [id]: e.target.checked } }))} />
                      Mute sounds from {p.name || `User ${id}`}
                    </label>
                  );
                })}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="vox-sb-search">
          <span aria-hidden="true">🔎</span>
          <input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search sounds..." aria-label="Search sounds" autoFocus={typeof window !== 'undefined' && window.innerWidth > 640} />
          {query ? (
            <button type="button" className="vox-sb-mini" style={{ position: 'static' }} aria-label="Clear search" onClick={() => setQuery('')}>
              ✕
            </button>
          ) : null}
        </div>

        {!q ? (
          <div className="vox-sb-tabs" role="tablist" aria-label="Sound groups">
            {visibleTabs.map((t) => (
              <button key={t.key} type="button" role="tab" className="vox-sb-tab" aria-selected={activeTab === t.key} onClick={() => setTab(t.key)}>
                {t.label}
                {byTab[t.key] && byTab[t.key].length ? <span className="n">{byTab[t.key].length}</span> : null}
              </button>
            ))}
          </div>
        ) : null}

        {banner ? (
          <div className={`vox-sb-notice ${banner.tone}`} role={banner.tone === 'err' ? 'alert' : 'status'}>
            <span>{banner.text}</span>
            {banner.action ? (
              <button type="button" className="coms-mini-btn tiny primary" onClick={banner.action[1]}>
                {banner.action[0]}
              </button>
            ) : null}
          </div>
        ) : null}
        {notice ? (
          <div className={`vox-sb-notice ${notice.tone}`} role={notice.tone === 'err' ? 'alert' : 'status'} data-testid="sb-notice">
            <span>{notice.text}</span>
          </div>
        ) : null}

        <div className="vox-sb-body" ref={bodyRef} onKeyDown={onGridKey}>
          {body()}
        </div>

        <div className="vox-sb-foot" aria-live="polite">
          <span className="grow">
            {coolLeft > 0 ? `Cooldown ${(coolLeft / 1000).toFixed(1)} s` : playingLine || (inVoice ? 'Click to play for everyone · ▶ to preview · right-click for more' : '▶ preview · join voice to play')}
          </span>
          <span style={{ display: 'none' }}>{tick}</span>
        </div>
      </div>

      {menu ? (
        <div className="vox-sb-menu" role="menu" style={{ left: menu.x, top: menu.y }} onMouseDown={(e) => e.stopPropagation()}>
          <button type="button" role="menuitem" disabled={!menu.item.playable} onClick={() => play(menu.item)}>
            Play for everyone
          </button>
          <button type="button" role="menuitem" onClick={() => { const it = menu.item; setMenu(null); preview(it); }}>
            Preview (only me)
          </button>
          <button type="button" role="menuitem" onClick={() => { const it = menu.item; setMenu(null); toggleFav(it); }}>
            {menu.item.favorite ? 'Remove from favourites' : 'Add to favourites'}
          </button>
          {menu.item.can_edit ? (
            <>
              <button type="button" role="menuitem" onClick={() => manage(menu.item)}>
                Edit…
              </button>
              {menu.item.kind === 'col' ? (
                <button type="button" role="menuitem" className="danger" onClick={() => { setConfirmDelete(menu.item); setMenu(null); }}>
                  Remove sound…
                </button>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}

      {confirmDelete ? (
        <div className="vox-sb-menu" role="alertdialog" aria-label="Remove sound" style={{ left: 16, bottom: 90, padding: 10, maxWidth: 300 }}>
          <div style={{ fontSize: 13, marginBottom: 8 }}>
            Remove “{confirmDelete.name}” from {confirmDelete.collection_name || 'its pack'}? Everyone who uses this pack loses it.
          </div>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <button type="button" className="coms-mini-btn" style={{ width: 'auto' }} onClick={() => setConfirmDelete(null)}>
              Keep
            </button>
            <button type="button" className="coms-mini-btn" style={{ width: 'auto', borderColor: 'rgba(237,66,69,0.7)' }} onClick={() => removeSound(confirmDelete)}>
              Remove
            </button>
          </div>
        </div>
      ) : null}
    </>,
    document.body
  );
}
